import { useCwL10n } from '@shared/use-cw-L10n.hook';
import { CWUser } from 'cw-isdk/core';
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { tap } from 'rxjs';
import Container from 'typedi';
import { LogInData } from './log-in.data';

const layoutId = 'coreAppLoginPage';

export function ImpersonationLoginModal(props: ImpersonationLoginModalProps): React.ReactElement {

    const cwL10n = useCwL10n();

    const [remainingTokenTime, setRemainingTokenTime] = useState('');
    const [isTokenExpired, setIsTokenExpired] = useState(false);
    const [doLoginSymbol, setDoLoginSymbol] = useState<symbol | undefined>(undefined);
    const [startCountdownSymbol] = useState<symbol | undefined>(() => {
        // Only start the countdown if there is no error.
        if (props.impersonationLoginInfo.Error == null) {
            return Symbol();
        } else {
            return undefined;
        }
    });

    useEffect(() => {
        if (doLoginSymbol == null || isTokenExpired) {
            return;
        }
        const loginData = Container.get(LogInData);
        const subscription = loginData.impersonationLogin(false).pipe(
            tap(loggedIn => {
                if(loggedIn) { props.onLoggedIn(); }
            })
        ).subscribe();
        return () => subscription.unsubscribe();
    }, [doLoginSymbol]);

    useEffect(() => {

        if (startCountdownSymbol == null) {
            return;
        }

        const intervalToken = setInterval(() => {
            const expirationInfo =getExpirationTimeRemaining(props.impersonationLoginInfo.Expires);
            setRemainingTokenTime(expirationInfo.formattedTime);
            if (expirationInfo.time <= 0) {
                setIsTokenExpired(true);
                clearInterval(intervalToken);
            }
        }, 200);
        return () => clearInterval(intervalToken);
    }, [startCountdownSymbol]);

    let body: React.ReactElement;
    if (props.impersonationLoginInfo.Error == null) {
        if (isTokenExpired || props.impersonationLoginInfo.Error === UserImpersonationErrorType.TokenExpired) {
            body = <div>{ cwL10n.get('The impersonation credentials have expired', 'text_body_impersonationTokenExpired', layoutId)}</div>;
        } else {

            const html = cwL10n.get(
                'A user impersonation credential was found.<br/>'
                    + 'You can log in as user <b>{$userName}</b> with user ID <b>{$userId}</b>.<br/><br/>'
                    + 'The impersonation credentials will expire in {$expirationTime}.',
                'text_body_loginAsUser',
                layoutId,
                {
                    userName: props.impersonationLoginInfo.UserName,
                    userId: props.impersonationLoginInfo.UserId,
                    expirationTime: remainingTokenTime
                }
            );

            body = (
                <div dangerouslySetInnerHTML={{ __html: html }}/>
            );
        }
    } else {

        const invalidTokenBody = <div>{ cwL10n.get('The impersonation credentials are invalid', 'text_body_impersonationTokenInvalid', layoutId)}</div>;

        switch(props.impersonationLoginInfo.Error) {
            case UserImpersonationErrorType.TokenHasInvalidSignature: // fall-thru
            case UserImpersonationErrorType.TokenIsInvalidFormat:
                body = invalidTokenBody;
                break;
            case UserImpersonationErrorType.UserNotValid:
                body = <div>{ cwL10n.get('The impersonation credentials do not belong to a user that can log in.', 'text_body_impersonationUserInvalid', layoutId) }</div>;
                break;
            default:
                body = invalidTokenBody;
                break;
        }
    }

    let loggedInAsMessage: React.ReactElement | undefined = undefined;
    if ((props.user?.EmployeeSid ?? 0) > 0) {

        const html = cwL10n.get(
            'You are currently logged in as <b>{ $loggedInAsUserName }</b>. Closing this window without '
                + 'logging in as the impersonation user will continue with <b>{ $loggedInAsUserName }</b>.',
            'text_body_currentlyLoggedInAs',
            layoutId,
            {
                loggedInAsUserName: props.user.LoginName
            }
        );

        loggedInAsMessage = (
            <>
                <br/>
                <div dangerouslySetInnerHTML={{ __html: html }}/>
            </>
        );
    }

    return (
        <Modal show={true}>
            <Modal.Header>{ cwL10n.get('User Impersonation', 'ctl_modal_modalUserImpersonation_title', layoutId)}</Modal.Header>
            <Modal.Body>
                {body}
                {loggedInAsMessage}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    disabled={isTokenExpired || props.impersonationLoginInfo.Error != null}
                    variant='danger'
                    data-cw-id='btnLoginAsUser'
                    onClick={() => setDoLoginSymbol(Symbol(''))}>
                    { cwL10n.get('Login', 'ctl_btn_btnLoginAsUser_buttonText', layoutId) }
                </Button>
                <Button
                    variant='primary'
                    data-cw-id='btnCloseLoginAsUser'
                    onClick={() => {
                        tryCancelImpersonationLogin();
                        props.onCancel();
                    }}
                    autoFocus={true}>
                    { cwL10n.get('Close', 'ctl_btn_btnCloseLoginAsUser_buttonText', layoutId) }
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function getExpirationTimeRemaining(expiration: number): { formattedTime: string; time: number } {

    // Subtracting a small amount to ensure
    // that the UI shows a sooner expiration than
    // actually exists to allow for excess request/response times.
    const fudgeFactor = 2;
    const totalSecondsRemaining = Math.floor(expiration - (Date.now()/1000) - fudgeFactor);
    if (totalSecondsRemaining <= 0) {
        return { formattedTime: '0:00', time: 0 };
    }

    const wholeMinutesRemaining = Math.floor(totalSecondsRemaining/60);
    const wholeSecondsRemaining = Math.round(totalSecondsRemaining - (wholeMinutesRemaining * 60));
    const seconds = new Intl.NumberFormat(undefined, { minimumIntegerDigits: 2 }).format(wholeSecondsRemaining);
    return {
        formattedTime: `${wholeMinutesRemaining}:${seconds}`,
        time: totalSecondsRemaining
    };
}

function tryCancelImpersonationLogin(): void {
    const loginData = Container.get(LogInData);
    loginData.impersonationLogin(true).subscribe();
}

export interface ImpersonationLoginModalProps {
    impersonationLoginInfo: ImpersonationLoginInfo;
    onCancel(): void;
    onLoggedIn(): void;
    user: CWUser | undefined;
}
export interface ImpersonationLoginInfo {
    Error: UserImpersonationErrorType | undefined;
    /**
     * This is UTC Unix epoch in seconds
     */
    Expires: number;
    UserName: string;
    UserId: string;
}

enum UserImpersonationErrorType {
    TokenExpired = 1,
    TokenIsInvalidFormat = 2,
    TokenHasInvalidSignature = 3,
    UserNotValid = 4
}
